



















































































































































































import { Component, Mixins, Prop, Ref, Watch } from "vue-property-decorator";
import { Mutation } from "vuex-class";

import DetailsModal from "@/components/DetailsModal.vue";
import DialogModal from "@/components/DialogModal.vue";
import VIcon from "@/components/VIcon.vue";

import ReportTemplateListMixin from "@/mixins/ReportTemplateListMixin";
import { AdcontrolProfile, ReportTemplate } from "@/models";
import {
  matchesTemplateQuery,
  templateDeletedMessage,
  templateDuplicatedMessage,
} from "./reportTemplate";
import TemplateDeleteModal from "./TemplateDeleteModal.vue";
import TemplateDuplicateModal from "./TemplateDuplicateModal.vue";

import TemplateCompetitorDetailsTable from "./TemplateCompetitorDetailsTable.vue";

@Component({
  components: {
    DetailsModal,
    DialogModal,
    VIcon,
    TemplateCompetitorDetailsTable,
    TemplateDeleteModal,
    TemplateDuplicateModal,
  },
})
export default class TemplateSelect extends Mixins(ReportTemplateListMixin) {
  @Prop({ required: true }) readonly profile!: AdcontrolProfile;
  @Prop({ default: false }) readonly generateMode!: boolean;
  @Prop({ default: false }) readonly scheduleMode!: boolean;
  @Prop({ default: false }) readonly actionsDisabled!: boolean;
  @Prop({ default: false }) readonly selectionMode!: boolean;
  @Prop({ default: null }) readonly usedTemplateId!: string | null;

  @Mutation("automaticReportsCache/invalidateAutomaticReportsCache")
  readonly invalidateAutomaticReportsCache!: () => void;

  @Ref("deleteModal") readonly deleteModal!: TemplateDeleteModal;
  @Ref("resultModal") readonly resultModal!: DialogModal;
  @Ref("duplicateModal") readonly duplicateModal!: TemplateDuplicateModal;

  templateToDelete: ReportTemplate | null = null;
  templateToDuplicate: ReportTemplate | null = null;
  searchQuery = "";
  selectedIds: string[] = [];

  isRowActionDisabled(template: ReportTemplate): boolean {
    return this.actionsDisabled || !this.belongsToProfile(template);
  }

  get selectAllIndeterminate(): boolean {
    return this.someSelectableSelected && !this.allSelectableSelected;
  }

  get selectAllDisabled(): boolean {
    return this.actionsDisabled || !this.selectableTemplates.length;
  }

  @Watch("selectionMode")
  onSelectionModeChange() {
    this.selectedIds = [];
    this.emitSelectionChange();
  }

  get selectedTemplates(): ReportTemplate[] {
    return this.templates.filter((t) => this.selectedIds.includes(t.id));
  }

  private emitSelectionChange() {
    this.$emit("selection-change", this.selectedTemplates);
  }

  isSelected(template: ReportTemplate): boolean {
    return this.selectedIds.includes(template.id);
  }

  toggleSelected(template: ReportTemplate) {
    this.selectedIds = this.isSelected(template)
      ? this.selectedIds.filter((id) => id !== template.id)
      : [...this.selectedIds, template.id];
    this.emitSelectionChange();
  }

  get selectableTemplates(): ReportTemplate[] {
    return this.displayedTemplates.filter((t) => this.belongsToProfile(t));
  }

  get allSelectableSelected(): boolean {
    return (
      this.selectableTemplates.length > 0 &&
      this.selectableTemplates.every((t) => this.isSelected(t))
    );
  }

  get someSelectableSelected(): boolean {
    return this.selectableTemplates.some((t) => this.isSelected(t));
  }

  toggleSelectAll() {
    const ids = this.selectableTemplates.map((t) => t.id);
    this.selectedIds = this.allSelectableSelected
      ? this.selectedIds.filter((id) => !ids.includes(id))
      : [
          ...this.selectedIds,
          ...ids.filter((id) => !this.selectedIds.includes(id)),
        ];
    this.emitSelectionChange();
  }

  protected onTemplatesFetched() {
    if (
      this.usedTemplateId !== null &&
      !this.templates.some((t) => t.id === this.usedTemplateId)
    ) {
      this.$emit("unuse");
    }
  }

  matchesQuery(template: ReportTemplate): boolean {
    return matchesTemplateQuery(template, this.searchQuery);
  }

  get filteredTemplates(): ReportTemplate[] {
    return this.sortedTemplates.filter((t) => this.matchesQuery(t));
  }

  get searchPlaceholder(): string {
    return this.selectionMode
      ? "Cerca i template da eliminare..."
      : "Cerca per nome...";
  }

  get displayedTemplates(): ReportTemplate[] {
    if (!this.selectionMode) return this.filteredTemplates;
    const own = this.templates.filter((t) => this.belongsToProfile(t));
    const others = this.templates.filter((t) => !this.belongsToProfile(t));
    return [
      ...own.filter((t) => this.matchesQuery(t)),
      ...own.filter((t) => !this.matchesQuery(t) && this.isSelected(t)),
      ...others.filter((t) => this.matchesQuery(t)),
    ];
  }

  openDuplicateConfirm(template: ReportTemplate) {
    this.templateToDuplicate = template;
    this.$nextTick(() => this.duplicateModal.show());
  }

  async confirmDuplicate(name: string) {
    const target = this.templateToDuplicate;
    if (!target) return;
    if (!(await this.duplicateTemplate(target, name))) return;
    this.duplicateModal.hide();
    this.templateToDuplicate = null;
    await this.resultModal.dialog(templateDuplicatedMessage(target, name));
  }

  openDeleteConfirm(template: ReportTemplate) {
    this.templateToDelete = template;
    this.$nextTick(() => this.deleteModal.show());
  }

  closeDeleteConfirm() {
    this.deleteModal.hide();
  }

  async confirmDelete() {
    if (!this.templateToDelete) return;
    const target = this.templateToDelete;
    this.deleting = true;
    try {
      const destroyed = await this.deleteTemplates([target.id], false);
      this.closeDeleteConfirm();
      if (!destroyed) return;
      if (target.id === this.usedTemplateId) {
        this.$emit("unuse");
      }
      this.invalidateAutomaticReportsCache();
      await this.resultModal.dialog(templateDeletedMessage(target));
    } finally {
      this.deleting = false;
      this.templateToDelete = null;
    }
  }

  get useButtonLabel(): string {
    if (!this.generateMode) return "Usa";
    return this.scheduleMode ? "Schedula" : "Genera Report";
  }

  isInUse(template: ReportTemplate): boolean {
    return (
      !this.generateMode &&
      this.usedTemplateId !== null &&
      template.id === this.usedTemplateId
    );
  }

  onUseClick(template: ReportTemplate) {
    this.$emit("use", template);
  }

  async onEditClick(template: ReportTemplate) {
    if (await this.warnIfEditBlocked(template)) return;
    this.$emit("edit", template.id);
  }

  get templatesToDelete(): ReportTemplate[] {
    return this.templateToDelete ? [this.templateToDelete] : [];
  }
}
